<script setup>
import { computed } from 'vue';
import { trackFromLevelCode, trackByCode, trackByLabel } from '@rm-ui/levelling/levellingMeta';

const props = defineProps({
  code: { type: String, default: '' },
  // Track metadata from the endpoint — the framework's own track (e.g. code 'S',
  // label 'Operational', its configured colour).
  trackCode: { type: String, default: '' },
  trackColour: { type: String, default: '' },
  trackLabel: { type: String, default: '' },
});

// Track badge. The COLOUR follows the design's fixed track palette, matched by
// code then label (so a client abbreviation like 'S' still maps to Operational's
// tint); the client's configured colour is only a last resort, then a neutral.
// The LETTER is the client's own track code/label initial, so it reads as the
// tenant configured it. Legacy rows with no track fall back to parsing the code.
const track = computed(() => {
  const mapped = trackByCode(props.trackCode) || trackByLabel(props.trackLabel);
  if (props.trackCode || props.trackLabel) {
    const src = props.trackCode || props.trackLabel;
    return {
      letter: String(src).trim().charAt(0).toUpperCase(),
      tint: (mapped && mapped.tint) || props.trackColour || '#5a6678',
      label: props.trackLabel || (mapped && mapped.label) || props.trackCode,
    };
  }
  return trackFromLevelCode(props.code);
});
</script>

<template>
  <span v-if="code" class="cc-levelchip">
    <span
      v-if="track"
      class="cc-levelchip-track"
      :style="{ background: track.tint }"
      :title="track.label"
    >{{ track.letter }}</span>
    {{ code }}
  </span>
  <span v-else class="cc-unlevelled">No level</span>
</template>

<style lang="scss" scoped>
/* Prototype lvl-levelchip — 12px/700, r7, 4px 8px 4px 7px; track 16×16 r4 9px/800. */
.cc-levelchip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 7px;
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 7px;
  background: var(--cc-surface-2, #ffffff);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  color: var(--cc-text, #333333);
  white-space: nowrap;

  .cc-levelchip-track {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    color: #ffffff;
    font-size: 9px;
    font-weight: 800;
    // Kill font line-height so the letter and the square share one optical
    // centre — otherwise the glyph's line-box drifts the letter off-centre.
    line-height: 1;
  }
}

/* Prototype lvl-unlevelled — dashed pill placeholder. */
.cc-unlevelled {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--cc-text-3, #97a0af);
  background: var(--cc-surface-2, #f7f9fb);
  border: 1px dashed var(--cc-border-strong, #d4dae6);
  border-radius: 999px;
  padding: 2px 9px;
}
</style>
